function readObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function readArray(value) {
  return Array.isArray(value) ? value : [];
}

export function resolveOrphanGapRecommendationEvidence(row) {
  const snapshot = readObject(row?.recommendation_inputs_snapshot);
  const strongWrapper = readObject(snapshot.orphan_gap_strong_action);
  const usesStrongDecision =
    Number(snapshot.contract_version) === 6 &&
    Number(strongWrapper.contract_version) === 1 &&
    Object.keys(readObject(strongWrapper.row_decision)).length > 0;
  const wrapper = usesStrongDecision
    ? strongWrapper
    : readObject(snapshot.orphan_gap_action);
  const decision = readObject(wrapper.row_decision);
  if (
    ![5, 6].includes(Number(snapshot.contract_version)) ||
    Number(wrapper.contract_version) !== 1 ||
    !Object.keys(decision).length
  ) {
    return null;
  }
  const baseDecision = usesStrongDecision
    ? readObject(decision.base_t08_decision)
    : decision;
  const horizon = readObject(baseDecision.horizon);
  const certification = readObject(baseDecision.certification);
  const t07Decision = readObject(
    certification.t07_decision || baseDecision.t07_decision
  );
  const t06Assessment = readObject(
    t07Decision.t06_assessment ||
      t07Decision.reassignment_assessment ||
      certification.t06_assessment
  );
  const demand = readObject(baseDecision.demand_evidence);
  const price = readObject(baseDecision.price_plan);
  const restriction = readObject(baseDecision.restriction_plan);
  const inventoryEvidence = readObject(decision.inventory_evidence);
  const approvalReasons = readArray(
    decision.approval_reasons ||
      decision.approval_reason_codes ||
      baseDecision.approval_reasons ||
      baseDecision.approval_reason_codes
  );
  const bindingConstraints = readArray(
    decision.binding_rule_codes ||
      decision.binding_constraints ||
      decision.bindings ||
      baseDecision.binding_constraints ||
      baseDecision.bindings
  );
  return {
    contractVersion: Number(wrapper.contract_version || 0),
    snapshotContractVersion: Number(snapshot.contract_version || 0),
    strongDecision: usesStrongDecision,
    action: decision.action ?? null,
    reasonCode: decision.reason_code ?? null,
    horizonBand:
      decision.horizon_band ?? horizon.band ?? baseDecision.horizon_band ?? null,
    leadDays:
      horizon.lead_days ?? baseDecision.t07_decision?.lead_days ?? null,
    gapNights:
      certification.gap_nights ??
      t07Decision.gap_nights ??
      baseDecision.identity?.gap_nights ??
      baseDecision.gap_nights ??
      null,
    t06Status:
      t06Assessment.status ??
      t06Assessment.assessment_status ??
      certification.status ??
      null,
    t06Eligible:
      t06Assessment.orphan_policy_eligible ??
      t06Assessment.is_feasible ??
      t06Assessment.is_reassignable ??
      t06Assessment.eligible ??
      null,
    paceStatus: demand.pace_status ?? demand.demand_status ?? null,
    paceGapPct: demand.pace_gap_pct ?? null,
    paceTolerancePct: demand.pace_tolerance_pct ?? null,
    paceExcessPct: demand.pace_excess_pct ?? null,
    weakDemand:
      demand.weak_demand_certified ??
      demand.weak_pace_certified ??
      demand.is_weak_demand ??
      (demand.pace_status == null ? null : demand.pace_status === "behind"),
    forecastConfidence:
      demand.forecast_confidence ??
      demand.confidence_evidence?.forecast_confidence ??
      null,
    featurePresenceScore:
      demand.feature_presence_score ??
      demand.confidence_evidence?.feature_presence_score ??
      null,
    effectiveConfidence:
      demand.effective_confidence ??
      demand.confidence_evidence?.effective_confidence ??
      null,
    lowConfidenceThreshold:
      demand.low_confidence_threshold ??
      demand.confidence_evidence?.low_confidence_threshold ??
      null,
    lowConfidence:
      demand.is_low_confidence ??
      demand.low_confidence ??
      demand.confidence_evidence?.is_low_confidence ??
      null,
    preOrphanPrice:
      price.pre_orphan_price ?? baseDecision.price_before_orphan ?? null,
    postOrphanPrice:
      price.post_orphan_price ?? baseDecision.price_after_orphan ?? null,
    effectiveFloorPrice:
      price.effective_floor_price ?? baseDecision.economic_floor_price ?? null,
    requestedDiscountPct:
      price.requested_discount_pct ?? baseDecision.requested_discount_pct ?? null,
    configuredDiscountPct:
      price.configured_discount_pct ??
      baseDecision.configured_discount_cap_pct ??
      null,
    paceDiscountPct: price.pace_discount_pct ?? null,
    headroomDiscountPct: price.headroom_discount_pct ?? null,
    effectiveDiscountPct:
      price.effective_discount_pct ?? baseDecision.effective_discount_pct ?? null,
    currency:
      price.currency_code ??
      wrapper.policy_snapshot?.currency_code ??
      baseDecision.near_policy_snapshot?.currency_code ??
      row?.room_rate_currency ??
      null,
    variableCost:
      price.variable_cost_per_occupied_room ??
      price.variable_cost ??
      wrapper.policy_snapshot?.variable_cost_per_occupied_room ??
      baseDecision.near_policy_snapshot?.variable_cost_per_occupied_room ??
      null,
    distributionCostPct:
      price.distribution_cost_pct ??
      wrapper.policy_snapshot?.distribution_cost_pct ??
      baseDecision.near_policy_snapshot?.distribution_cost_pct ??
      null,
    minLosBefore:
      restriction.min_los_before ?? baseDecision.current_min_stay ?? null,
    minLosAfter:
      restriction.min_los_after ?? baseDecision.recommended_min_stay ?? null,
    maxLosBefore:
      restriction.max_los_before ?? baseDecision.current_max_stay ?? null,
    maxLosAfter:
      restriction.max_los_after ?? baseDecision.recommended_max_stay ?? null,
    ctaCtdChanged:
      restriction.cta_ctd_changed ??
      (baseDecision.cta_ctd_unchanged == null
        ? null
        : baseDecision.cta_ctd_unchanged !== true),
    approvalRequired:
      decision.approval_required === true ||
      baseDecision.approval_required === true,
    approvalReasons,
    bindingConstraints,
    activationApplied: decision.activation_applied === true,
    currentAppliedPrice: decision.current_applied_price ?? null,
    priceBeforeStrong: decision.price_before_strong ?? null,
    protectionAnchorPrice: decision.protection_anchor_price ?? null,
    priceAfterStrong: decision.price_after_strong ?? null,
    requestedPremiumPct:
      decision.requested_premium_pct ??
      decision.configured_premium_cap_pct ??
      null,
    configuredPremiumCapPct: decision.configured_premium_cap_pct ?? null,
    pacePremiumCapPct: decision.pace_premium_cap_pct ?? null,
    ceilingPremiumCapPct: decision.ceiling_premium_cap_pct ?? null,
    movementPremiumCapPct: decision.movement_premium_cap_pct ?? null,
    effectivePremiumPct: decision.effective_premium_pct ?? null,
    protectionLiftPct: decision.protection_lift_pct ?? null,
    totalStrongUpliftPct: decision.total_strong_uplift_pct ?? null,
    strongPaceState: decision.pace_state ?? null,
    inventoryReady: inventoryEvidence.is_ready ?? null,
    lastRoomAvailable: inventoryEvidence.last_room_available ?? null,
    noLongerStayDisplacement:
      inventoryEvidence.no_longer_stay_displacement ?? null,
    strongEffectiveDiscountPct: decision.effective_discount_pct ?? null,
    restrictionsUnchangedFromT08:
      decision.restrictions_unchanged_from_t08 ?? null,
    ctaCtdUnchanged: decision.cta_ctd_unchanged ?? null,
    stopSellUnchanged: decision.stop_sell_unchanged ?? null,
    automaticApplicationAllowed:
      decision.automatic_application_allowed ?? null,
    priceApplied: decision.price_applied === true,
    restrictionsApplied: decision.restrictions_applied === true,
  };
}
